import React, {
  createContext,
  useContext,
  ReactNode,
  useState,
  useEffect,
} from 'react';
import { isCountryAuthorized, IpCheckResult } from '../../utils/isCountryAuthorized';
import { isUnreachableError, markBackendDown, markBackendUp } from '../../utils/backendStatus';

/**
 * Interface representing the cached compliance data.
 */
interface AccessControlContextType {
  accessGranted: boolean;
  countryIso2: string | null;   // Corresponds to countryCode from API
  countryName: string | null;
  ip: string | null;
  isLoading: boolean;           // Useful for disabling forms while checking
}

const COUNTRY_STORAGE_KEY = 'countryAuthorization';

const AccessControlContext = createContext<AccessControlContextType | undefined>(undefined);

/**
 * Custom hook to access cached localization and compliance data.
 */
export const useAccessControl = () => {
  const context = useContext(AccessControlContext);
  if (!context) {
    throw new Error('useAccessControl must be used within an AccessControlProvider');
  }
  return context;
};

export const AccessControlProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [accessGranted, setAccessGranted] = useState<boolean>(true);
  const [countryIso2, setCountryIso2] = useState<string | null>(null);
  const [countryName, setCountryName] = useState<string | null>(null);
  const [ip, setIp] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    // Skip geo check if env variable is set (for local development)
    if (process.env.REACT_APP_SKIP_GEO_CHECK === 'true') {
      setAccessGranted(true);
      return;
    }

    /**
     * Helper to update both React state and LocalStorage cache.
     */
    const updateCache = (data: IpCheckResult) => {
      setAccessGranted(data.allowed);
      setCountryIso2(data.countryCode);
      setCountryName(data.countryName);
      setIp(data.ip);

      if (typeof window !== 'undefined') {
        localStorage.setItem(COUNTRY_STORAGE_KEY, JSON.stringify(data));
      }
    };

    // 1) Immediate hydration from LocalStorage
    if (typeof window !== 'undefined') {
      const savedData = localStorage.getItem(COUNTRY_STORAGE_KEY);
      if (savedData) {
        try {
          updateCache(JSON.parse(savedData));
        } catch (e) {
          console.error('Invalid cache format', e);
        }
      }
    }

    // 2) Background refresh from API
    const fetchLatestStatus = async () => {
      try {
        setIsLoading(true);
        const result = await isCountryAuthorized();
        markBackendUp();
        updateCache(result);
      } catch (error) {
        if (isUnreachableError(error)) {
          // The API did not answer (deploy, gateway restart). That is not a compliance decision:
          // the maintenance screen takes over the whole app, and the cached verdict is kept for
          // when it comes back.
          markBackendDown();
        } else {
          setAccessGranted(false); // Security: block if the check itself fails
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchLatestStatus();
  }, []);

  return (
    <AccessControlContext.Provider value={{ accessGranted, countryIso2, countryName, ip, isLoading }}>
      {children}
    </AccessControlContext.Provider>
  );
};